import { useState } from 'react';

import uploadIndividualFileToStorage from '@client/helpers/firebase/storage/uploadIndividualFileToStorage';

import { type FirebaseStorage, getDownloadURL, ref, type UploadTask } from 'firebase/storage';

type Props = {
  onComplete?: (task: UploadTask) => void;
  onStart?: () => void;
  onError?: (error: Error) => void;
  onProgress?: (uploadTask: UploadTask, progress: number) => void;
};

type UploadFileOtions = {
  keepFileName?: boolean;
  originalFilename?: boolean;
};

export const useFileStorageHelper = ({ onComplete, onStart, onError, onProgress }: Props = {}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  /**
   *  Uploads a file to firebase storage and returns the download url and the file
   * @param path - path to the folder in firebase storage where the file should be uploaded to
   * @param fileName - name of the file
   * @param file - file to be uploaded
   * @returns - download url and the file
   */
  const uploadFile = async (
    path: string,
    fileName: string,
    file: File,
    options?: UploadFileOtions,
  ) => {
    const { originalFilename = false, keepFileName = true } = options ?? {};
    const storageRef = `${path}`;

    try {
      // console.log("uploading file...");
      const { file: fileUploaded, imagePath } = await new Promise<{
        imagePath: string | undefined;
        file: File;
      }>((resolve, reject) => {
        uploadIndividualFileToStorage(file, {
          storageRef,
          onStart: () => {
            onStart && onStart();
            setIsUploading(true);
            setProgress(0);
          },
          onComplete: async (imagePath, file, uploadTask) => {
            onComplete && onComplete(uploadTask);
            // console.log("upload complete", {
            //   imagePath,
            //   task: uploadTask.snapshot.state,
            // });
            if (uploadTask.snapshot.state === 'success') {
              // console.log("upload success");
              setIsUploading(false);
              resolve({ imagePath, file });
            } else {
              console.log('upload failed');
              setIsUploading(false);
              reject(new Error('Upload failed'));
            }
          },
          fileName: originalFilename ? fileName : fileName + `.${file.name.split('.').pop()}`,
          keepFileName,
        }).catch(error => {
          onError && onError(error);
          reject(error);
        });
      });

      return { file: fileUploaded, imagePath };
    } catch (error) {
      // TODO Improve error handling
      console.error(error);
      throw error;
    }
  };

  const fetchFile = async (storage: FirebaseStorage, path: string) => {
    const storageRef = ref(storage, path);
    const url = await getDownloadURL(storageRef);
    const blob = await fetch(url).then(r => r.blob());

    // console.log("blob: ", blob);
    return new File([blob], path, { type: blob.type });
  };

  return { uploadFile, fetchFile, isUploading, progress };
};
